import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Table,
  Button,
  Modal,
  Form,
  Alert,
  Spinner,
  Badge,
  Row,
  Col,
  Card
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { analysisPromptAPI } from '../utils/api';
import { getPromptType } from '../utils/analysisPrompt';
import type { AnalysisPrompt, AnalysisPromptType, CreateAnalysisPromptRequest, UpdateAnalysisPromptRequest } from '../types/snapshot';

const DESK_OCCUPANCY_STARTER_PROMPT = `Analyze the camera snapshot and report the occupancy of each desk in the room.

For every desk visible in the image:
1. Determine whether the desk is currently occupied or unoccupied.
2. Provide the bounding box of each desk in the image.
3. If a desk is unoccupied and was occupied before, report how long it has been idle.

Return your answer as structured detections; the backend appends the JSON schema automatically. Do not invent desks that are not visible.`;

const DESK_ACTIVITY_STARTER_PROMPT = `You will receive 2–3 top-down CCTV frames of the same workspace, taken a few seconds apart, oldest first. Detect every workstation as in desk occupancy, and draw each box on Frame 1.

For each workstation:

Status: Occupied if a person is seated or working there, Unoccupied if nobody is there, Unknown if you cannot tell.

Activity: compare the frames inside that desk box only.

Working: hands, tools, or the work piece change position between frames.

Idle: a person is at the desk, but hands, tools, and the work piece do not change between frames.

Away: nobody is at the desk.

Unknown: the view is blurred or blocked, or the frames cannot be compared.

Ignore people walking past, movement at other desks, screens, and light changes.

Do not report minutes or history. The system calculates time.`;

const PromptTypeBadge: React.FC<{ promptType: AnalysisPromptType; t: ReturnType<typeof useTranslation>['t'] }> = ({ promptType, t }) => (
  <Badge bg={promptType === 'desk_occupancy' ? 'info' : promptType === 'desk_activity' ? 'success' : 'secondary'}>
    {promptType === 'desk_occupancy' ? t('promptTypeDeskOccupancy') : promptType === 'desk_activity' ? t('promptTypeDeskActivity') : t('promptTypeGeneric')}
  </Badge>
);

interface PromptCardProps {
  prompt: AnalysisPrompt;
  onEdit: (prompt: AnalysisPrompt) => void;
  onDelete: (prompt: AnalysisPrompt) => void;
  t: ReturnType<typeof useTranslation>['t'];
  tc: ReturnType<typeof useTranslation>['t'];
}

const PromptCard: React.FC<PromptCardProps> = ({ prompt, onEdit, onDelete, t, tc }) => (
  <Card className="an-data-card border-0 rounded-4 mb-3">
    <Card.Body className="p-3">
      <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
        <div>
          <div className="fw-bold mb-1">{prompt.name}</div>
          <div className="small text-muted">{t('sortOrder')}: {prompt.sort_order}</div>
        </div>
        <div className="d-flex flex-column align-items-end gap-1">
          <PromptTypeBadge promptType={getPromptType(prompt)} t={t} />
          <Badge bg={prompt.active ? 'success' : 'secondary'}>
            {prompt.active ? t('active') : tc('labels.inactive')}
          </Badge>
        </div>
      </div>

      <div className="d-flex flex-wrap gap-2 justify-content-end">
        <Button
          variant="link"
          className="text-primary p-1 text-decoration-none"
          onClick={() => onEdit(prompt)}
        >
          <i className="bi bi-pencil"></i> {tc('buttons.edit')}
        </Button>
        <Button
          variant="link"
          className="text-danger p-1 text-decoration-none"
          onClick={() => onDelete(prompt)}
        >
          <i className="bi bi-trash"></i> {tc('buttons.delete')}
        </Button>
      </div>
    </Card.Body>
  </Card>
);

const AnalysisPrompts: React.FC = () => {
  const { currentCompany } = useAuth();
  const { isSuperAdmin } = useRolePermissions();
  const { t } = useTranslation('pages', { keyPrefix: 'analysisPrompts' });
  const { t: tc } = useTranslation('common');

  const [prompts, setPrompts] = useState<AnalysisPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<AnalysisPrompt | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState<CreateAnalysisPromptRequest>({
    name: '',
    system_prompt: '',
    active: true,
    sort_order: 0,
    prompt_type: 'generic'
  });

  // Delete state
  const [showDeleteModal, setShowDeleteModal] = useState<AnalysisPrompt | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchPrompts = useCallback(async () => {
    if (!currentCompany) return;
    try {
      setLoading(true);
      setError(null);
      const res = await analysisPromptAPI.listPrompts(currentCompany.company_id);
      setPrompts(res.data || []);
    } catch (err) {
      console.error('Error fetching prompts:', err);
      setError(t('fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [currentCompany, t]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchPrompts();
    }
  }, [fetchPrompts, isSuperAdmin]);

  const handleOpenModal = (prompt?: AnalysisPrompt) => {
    if (prompt) {
      setEditingPrompt(prompt);
      setFormData({
        name: prompt.name,
        system_prompt: prompt.system_prompt,
        active: prompt.active,
        sort_order: Number(prompt.sort_order) || 0,
        prompt_type: getPromptType(prompt)
      });
    } else {
      setEditingPrompt(null);
      setFormData({
        name: '',
        system_prompt: '',
        active: true,
        sort_order: 0,
        prompt_type: 'generic'
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPrompt(null);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentCompany) return;

    try {
      setSubmitting(true);
      setError(null);
      
      if (editingPrompt) {
        await analysisPromptAPI.updatePrompt(currentCompany.company_id, editingPrompt.id, formData as UpdateAnalysisPromptRequest);
        setSuccess(t('updateSuccess'));
      } else {
        await analysisPromptAPI.createPrompt(currentCompany.company_id, formData);
        setSuccess(t('createSuccess'));
      }
      
      handleCloseModal();
      fetchPrompts();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving prompt:', err);
      setError(editingPrompt ? t('updateFailed') : t('createFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!currentCompany || !showDeleteModal) return;

    try {
      setDeleting(true);
      setError(null);
      await analysisPromptAPI.deletePrompt(currentCompany.company_id, showDeleteModal.id);
      setSuccess(t('deleteSuccess'));
      setShowDeleteModal(null);
      fetchPrompts();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting prompt:', err);
      setError(t('deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <Layout title={t('title')}>
        <Container className="an-enterprise-page an-page-shell an-data-page visioniq-page">
          <Alert variant="danger">{t('accessDenied')}</Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={t('title')}>
      <Container fluid className="an-enterprise-page an-page-shell an-data-page visioniq-page analysis-prompts-page">
        <div className="page-header">
          <h2 className="fw-bold mb-0 an-page-title">{t('title')}</h2>
          <Button variant="primary" onClick={() => handleOpenModal()}>
            <i className="bi bi-plus-lg me-2"></i>{t('addPrompt')}
          </Button>
        </div>

        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : prompts.length === 0 ? (
          <div className="text-center py-5 an-data-empty-state">
            <i className="bi bi-chat-left-text text-muted mb-3" style={{ fontSize: '3rem' }}></i>
            <h4>{t('noPrompts')}</h4>
            <p className="text-muted">{t('noPromptsHint')}</p>
            <Button variant="primary" onClick={() => handleOpenModal()} className="mt-2">
              {t('addPrompt')}
            </Button>
          </div>
        ) : (
          <>
            <div className="d-md-none">
              {[...prompts].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map(prompt => (
                <PromptCard
                  key={prompt.id}
                  prompt={prompt}
                  onEdit={handleOpenModal}
                  onDelete={setShowDeleteModal}
                  t={t}
                  tc={tc}
                />
              ))}
            </div>

            <div className="d-none d-md-block">
              <div className="table-responsive an-data-table analysis-prompts-table an-panel">
                <Table hover className="mb-0 align-middle">
                    <thead className="bg-light">
                    <tr>
                      <th className="px-4 py-3">{t('name')}</th>
                      <th className="py-3 text-center">{t('promptType')}</th>
                      <th className="py-3 text-center">{t('active')}</th>
                      <th className="py-3 text-center">{t('sortOrder')}</th>
                      <th className="px-4 py-3 text-end">{tc('labels.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...prompts].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map(prompt => (
                      <tr key={prompt.id}>
                        <td className="px-4 py-3 align-middle fw-medium">{prompt.name}</td>
                        <td className="py-3 align-middle text-center">
                          <PromptTypeBadge promptType={getPromptType(prompt)} t={t} />
                        </td>
                        <td className="py-3 align-middle text-center">
                          <Badge bg={prompt.active ? 'success' : 'secondary'}>
                            {prompt.active ? t('active') : tc('labels.inactive')}
                          </Badge>
                        </td>
                        <td className="py-3 align-middle text-center">{prompt.sort_order}</td>
                        <td className="px-4 py-3 align-middle text-end">
                          <Button 
                            variant="link" 
                            className="text-primary p-1 me-3 text-decoration-none"
                            onClick={() => handleOpenModal(prompt)}
                          >
                            <i className="bi bi-pencil"></i> {tc('buttons.edit')}
                          </Button>
                          <Button 
                            variant="link" 
                            className="text-danger p-1 text-decoration-none"
                            onClick={() => setShowDeleteModal(prompt)}
                          >
                            <i className="bi bi-trash"></i> {tc('buttons.delete')}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          </>
        )}

        {/* Add/Edit Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>{editingPrompt ? t('editPrompt') : t('addPrompt')}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{t('name')}</Form.Label>
                <Form.Control
                  type="text"
                  required
                  maxLength={255}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('namePlaceholder')}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{t('promptType')}</Form.Label>
                <Form.Select
                  value={formData.prompt_type || 'generic'}
                  onChange={(e) => setFormData({ ...formData, prompt_type: e.target.value as AnalysisPromptType })}
                >
                  <option value="generic">{t('promptTypeGeneric')}</option>
                  <option value="desk_occupancy">{t('promptTypeDeskOccupancy')}</option>
                  <option value="desk_activity">{t('promptTypeDeskActivity')}</option>
                </Form.Select>
                {formData.prompt_type === 'desk_occupancy' && (
                  <div className="mt-2">
                    <small className="text-muted d-block">{t('promptTypeHint')}</small>
                    <Button
                      variant="outline-info"
                      size="sm"
                      className="mt-2"
                      onClick={() => {
                        if (!formData.system_prompt.trim()) {
                          setFormData({ ...formData, system_prompt: DESK_OCCUPANCY_STARTER_PROMPT });
                        }
                      }}
                    >
                      <i className="bi bi-magic me-1"></i>{t('useStarterTemplate')}
                    </Button>
                  </div>
                )}
                {formData.prompt_type === 'desk_activity' && (
                  <div className="mt-2">
                    <small className="text-muted d-block">{t('promptTypeActivityHint')}</small>
                    <Button variant="outline-success" size="sm" className="mt-2" onClick={() => {
                      if (!formData.system_prompt.trim()) setFormData({ ...formData, system_prompt: DESK_ACTIVITY_STARTER_PROMPT });
                    }}>
                      <i className="bi bi-magic me-1"></i>{t('useStarterTemplate')}
                    </Button>
                  </div>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{t('systemPrompt')}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={8}
                  required
                  maxLength={5000}
                  value={formData.system_prompt}
                  onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
                  placeholder={t('systemPromptPlaceholder')}
                />
                <div className="d-flex justify-content-between mt-1">
                  <small className="text-muted">{t('systemPromptHint')}</small>
                  <small className={formData.system_prompt.length > 4800 ? 'text-danger fw-bold' : 'text-muted'}>
                    {t('charactersRemaining', { count: 5000 - formData.system_prompt.length })}
                  </small>
                </div>
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">{t('sortOrder')}</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.sort_order}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        setFormData({ ...formData, sort_order: isNaN(val) ? 0 : val });
                      }}
                    />
                    <small className="text-muted">{t('sortOrderHint')}</small>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold d-block">{t('active')}</Form.Label>
                    <Form.Check 
                      type="switch"
                      id="active-switch"
                      label={formData.active ? t('active') : tc('labels.inactive')}
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    />
                    <small className="text-muted">{t('activeHint')}</small>
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal} disabled={submitting}>
                {tc('buttons.cancel')}
              </Button>
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? <Spinner animation="border" size="sm" /> : tc('buttons.save')}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Delete Modal */}
        <Modal show={!!showDeleteModal} onHide={() => setShowDeleteModal(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{tc('buttons.delete')} {t('title')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
            <p>{t('deleteConfirm')}</p>
            {showDeleteModal && (
              <div className="p-3 bg-light rounded border text-center fw-bold an-panel">
                {showDeleteModal.name}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(null)} disabled={deleting}>
              {tc('buttons.cancel')}
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? <Spinner animation="border" size="sm" /> : tc('buttons.delete')}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </Layout>
  );
};

export default AnalysisPrompts;
